import { useDeviceHistory, type DeviceEvent } from "../api/devices";

// Visual treatment per event type. Unknown types fall back to the raw type
// string with a neutral dot, so a new backend event kind still renders.
const TYPE_META: Record<string, { label: string; dot: string }> = {
  connected: { label: "Connected", dot: "bg-green-500" },
  disconnected: { label: "Disconnected", dot: "bg-gray-400" },
  // NOT a check-in marker, despite the temptation to label it one: this is
  // recorded only when a MIRRORED field actually changes (firmware version,
  // model, hardware, check-in cadence). A device that checks in and reports
  // the same state produces no event at all, so labelling it "Checked in"
  // would imply an absence means the device never called home.
  shadow_reported: { label: "Reported state", dot: "bg-blue-500" },
  firmware_url: { label: "Firmware download", dot: "bg-amber-500" },
  config_pushed: { label: "Config pushed", dot: "bg-purple-500" },
  // The device's own account of taking a config change — distinct from
  // config_pushed, which is the cloud writing desired state. A push with no
  // apply after it means the device never took the change.
  config_applied: { label: "Config applied", dot: "bg-emerald-500" },
  // A change made ON the device (e.g. the gateway's own web page). Local wins:
  // the platform adopts it as the device's configuration. The message says
  // when it could not, because the next dashboard save would then undo it.
  config_changed_locally: { label: "Changed on device", dot: "bg-sky-500" },
  job_status: { label: "Update status", dot: "bg-riot-gold-500" },
};

function metaFor(type: string) {
  return TYPE_META[type] ?? { label: type, dot: "bg-gray-300" };
}

// detailSummary renders the structured detail map compactly (k=v, k=v).
function detailSummary(detail?: Record<string, string>): string {
  if (!detail) return "";
  return Object.entries(detail)
    .map(([k, v]) => `${k}=${v}`)
    .join("  ");
}

export function DeviceEventLog({ imei }: { imei: string }) {
  const { data, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage } = useDeviceHistory(imei, { limit: 50 });
  // Be defensive: only an array is iterable. Guards against an unexpected
  // response shape so the page never crashes on a malformed payload.
  const events: DeviceEvent[] = (data?.pages ?? []).flatMap((page) => (Array.isArray(page?.data) ? page.data : []));

  return (
    <section
      aria-label="Device activity log"
      className="mt-4 rounded-lg border border-gray-200 bg-white p-5 dark:border-riot-navy-700 dark:bg-riot-navy-800"
    >
      <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Activity log</h3>

      {isLoading && (
        <p className="text-xs text-gray-400 dark:text-riot-navy-400">Loading activity…</p>
      )}
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400">Failed to load activity</p>
      )}
      {!isLoading && !error && events.length === 0 && (
        <p className="text-xs text-gray-400 dark:text-riot-navy-400">No recent activity</p>
      )}

      {events.length > 0 && (
        <ol className="space-y-2">
          {events.map((e, i) => {
            const meta = metaFor(e.type);
            const summary = e.message || detailSummary(e.detail);
            return (
              <li key={`${e.timestamp}-${e.type}-${i}`} className="flex items-start gap-2">
                <span className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${meta.dot}`} aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs font-medium text-gray-900 dark:text-riot-navy-100">{meta.label}</span>
                    <time className="flex-shrink-0 text-[11px] text-gray-400 dark:text-riot-navy-400" dateTime={e.timestamp}>
                      {new Date(e.timestamp).toLocaleString()}
                    </time>
                  </div>
                  {summary && (
                    <p className="truncate text-[11px] text-gray-500 dark:text-riot-navy-300">{summary}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {hasNextPage && (
        <button
          type="button"
          onClick={() => fetchNextPage()}
          disabled={isFetchingNextPage}
          className="mt-3 text-xs font-medium text-riot-gold-600 hover:text-riot-gold-700 disabled:opacity-50 dark:text-riot-gold-400"
        >
          {isFetchingNextPage ? "Loading…" : "Load older activity"}
        </button>
      )}
    </section>
  );
}
